/**
 * Shared carousel lightbox for product photos and finish swatches.
 * API: mspLightbox.open(items, index), items = [{ url, caption }]
 * Viewport-max display (never upscale past native) + optional hover magnifier.
 */
(function () {
  var ZOOM_FACTOR = 2.5;
  var LENS_RATIO = 0.12;
  var LENS_MIN_PX = 52;
  var LENS_MAX_PX = 96;
  var MIN_MAGNIFY_PX = 100;
  var PANE_MAX_PX = 420;
  var PANE_MIN_PX = 240;
  var PANE_MIN_VIEWPORT = 640;

  function boot() {
    var root = document.getElementById('msp-lightbox');
    if (!root) return;

    var items = [];
    var index = 0;
    var imgEl = root.querySelector('.msp-lightbox__img');
    var viewportEl = root.querySelector('.msp-lightbox__zoom-viewport');
    var mediaEl = root.querySelector('.msp-lightbox__media');
    var lensEl = root.querySelector('.msp-lightbox__lens');
    var zoomPaneEl = root.querySelector('.msp-lightbox__zoom-pane');
    var capEl = root.querySelector('.msp-lightbox__caption');
    var frameEl = root.querySelector('.msp-lightbox__frame');
    var stageEl = root.querySelector('.msp-lightbox__stage');
    var prevBtn = root.querySelector('.msp-lightbox__nav--prev');
    var nextBtn = root.querySelector('.msp-lightbox__nav--next');
    var closeBtn = root.querySelector('.msp-lightbox__close');
    var backdrop = root.querySelector('.msp-lightbox__backdrop');
    var keyHandler = null;
    var resizeHandler = null;
    var magnifierMove = null;
    var magnifierLeave = null;
    var activeZoomLevel = 1;

    function canUseMagnifier() {
      if (!window.matchMedia) return true;
      return !window.matchMedia('(hover: none)').matches;
    }

    /** Cached PDP HTML may still ship lens/pane with the hidden attribute (overrides .is-visible). */
    function revealMagnifierEl(el) {
      if (!el) return;
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
    }

    /** Prefer full-size CDN URL (thumbs often pass _small / _1024x1024 variants). */
    function upgradeImageUrl(url) {
      if (!url || typeof url !== 'string') return url || '';
      var out = url.replace(
        /_(?:pico|icon|thumb|small|compact|medium|large|grande|master|original|\d+x\d+)(?=\.[^.?]+(?:\?|$))/i,
        ''
      );
      return out;
    }

    function computeZoomLevel(nw, nh, cw, ch) {
      if (!nw || !nh || !cw || !ch) return 0;
      if (Math.max(nw, nh) < MIN_MAGNIFY_PX) return 0;
      var nativeHeadroom = Math.min(nw / cw, nh / ch);
      if (nativeHeadroom >= 1.05) return Math.min(ZOOM_FACTOR, nativeHeadroom);
      if (Math.max(nw, nh) >= 400) return 2;
      return 1.65;
    }

    function resetImageTransform() {
      if (!imgEl) return;
      imgEl.style.transform = '';
      imgEl.style.transformOrigin = '';
    }

    function syncViewportSize() {
      if (!viewportEl || !imgEl) return;
      var cw = imgEl.clientWidth || imgEl.offsetWidth;
      var ch = imgEl.clientHeight || imgEl.offsetHeight;
      if (!cw || !ch) return;
      viewportEl.style.width = cw + 'px';
      viewportEl.style.height = ch + 'px';
    }

    function normalizeItem(raw) {
      if (!raw) return { url: '', caption: '' };
      if (typeof raw === 'string') {
        return { url: upgradeImageUrl(raw), caption: '' };
      }
      return {
        url: upgradeImageUrl(raw.url || raw.src || ''),
        caption: raw.caption || raw.name || raw.alt || '',
      };
    }

    function viewportWidth() {
      return window.innerWidth || document.documentElement.clientWidth;
    }

    function viewportHeight() {
      return window.innerHeight || document.documentElement.clientHeight;
    }

    function isSwatchMode() {
      return root.classList.contains('msp-lightbox--swatch');
    }

    function estimateCaptionHeight() {
      if (!capEl || !capEl.textContent) return 0;
      if (capEl.offsetHeight > 0) return capEl.offsetHeight;
      return isSwatchMode() ? 28 : 52;
    }

    function getAvailableBox() {
      var vw = viewportWidth();
      var vh = viewportHeight();
      var captionH = estimateCaptionHeight();
      var overlayPadV = 48;
      var overlayPadH = 32;
      var chromeV = 24;
      var maxW;
      var maxH;

      if (isSwatchMode()) {
        maxW = Math.min(vw * 0.85, 480) - overlayPadH;
        maxH = vh - overlayPadV - captionH - chromeV;
        maxH = Math.min(maxH, 480);
      } else {
        var navW = prevBtn ? prevBtn.offsetWidth : 48;
        var gap = stageEl ? parseInt(getComputedStyle(stageEl).gap, 10) || 12 : 12;
        if (isNaN(gap)) gap = 12;
        var navChrome = (navW + gap) * 2 + 16;
        maxW = vw - overlayPadH - navChrome;
        maxH = vh - overlayPadV - captionH - chromeV;
      }

      return {
        maxW: Math.max(80, Math.floor(maxW)),
        maxH: Math.max(80, Math.floor(maxH)),
      };
    }

    function teardownMagnifier() {
      root.classList.remove('msp-lightbox--magnify', 'is-magnifying');
      activeZoomLevel = 1;
      resetImageTransform();
      if (viewportEl) {
        viewportEl.style.width = '';
        viewportEl.style.height = '';
      }
      if (lensEl) {
        lensEl.classList.remove('is-visible');
        lensEl.removeAttribute('style');
      }
      if (zoomPaneEl) {
        zoomPaneEl.classList.remove('is-visible');
        zoomPaneEl.style.backgroundImage = '';
        zoomPaneEl.removeAttribute('style');
      }
      if (magnifierMove) {
        if (mediaEl) {
          mediaEl.removeEventListener('mousemove', magnifierMove);
          mediaEl.removeEventListener('mouseenter', magnifierMove);
          mediaEl.removeEventListener('mouseleave', magnifierLeave);
        }
        if (imgEl) {
          imgEl.removeEventListener('mousemove', magnifierMove);
          imgEl.removeEventListener('mouseenter', magnifierMove);
          imgEl.removeEventListener('mouseleave', magnifierLeave);
        }
      }
      magnifierMove = null;
      magnifierLeave = null;
    }

    function clearFit() {
      teardownMagnifier();
      if (imgEl) {
        imgEl.style.maxWidth = '';
        imgEl.style.maxHeight = '';
        imgEl.onload = null;
      }
    }

    function positionZoomPane() {
      if (!zoomPaneEl || !frameEl) return;
      var paneW = zoomPaneEl.offsetWidth;
      var paneH = zoomPaneEl.offsetHeight;
      var frameRect = frameEl.getBoundingClientRect();
      var vw = viewportWidth();
      var vh = viewportHeight();
      var margin = 16;
      var left = frameRect.right + margin;
      var top = Math.max(margin, frameRect.top);

      if (left + paneW > vw - margin) {
        left = frameRect.left - paneW - margin;
      }
      if (left < margin) {
        left = Math.max(margin, (vw - paneW) / 2);
        top = Math.max(margin, frameRect.bottom + margin);
      }
      if (top + paneH > vh - margin) {
        top = Math.max(margin, vh - paneH - margin);
      }

      zoomPaneEl.style.left = left + 'px';
      zoomPaneEl.style.top = top + 'px';
    }

    function setupMagnifierIfEligible(attempt) {
      var tryNum = attempt || 0;
      if (!root.classList.contains('is-open')) return;
      /* Product gallery only; finish color swatches stay a simple viewer. */
      if (isSwatchMode()) return;

      if (!canUseMagnifier() || !imgEl || !mediaEl) {
        return;
      }

      if (magnifierMove) {
        if (mediaEl) {
          mediaEl.removeEventListener('mousemove', magnifierMove);
          mediaEl.removeEventListener('mouseenter', magnifierMove);
          mediaEl.removeEventListener('mouseleave', magnifierLeave);
        }
        if (imgEl) {
          imgEl.removeEventListener('mousemove', magnifierMove);
          imgEl.removeEventListener('mouseenter', magnifierMove);
          imgEl.removeEventListener('mouseleave', magnifierLeave);
        }
        magnifierMove = null;
        magnifierLeave = null;
      }

      var nw = imgEl.naturalWidth;
      var nh = imgEl.naturalHeight;
      var cw = imgEl.clientWidth || imgEl.offsetWidth;
      var ch = imgEl.clientHeight || imgEl.offsetHeight;

      if ((!nw || !nh || !cw || !ch) && tryNum < 24) {
        requestAnimationFrame(function () {
          setupMagnifierIfEligible(tryNum + 1);
        });
        return;
      }

      if (!nw || !nh || !cw || !ch) return;

      activeZoomLevel = computeZoomLevel(nw, nh, cw, ch);
      if (activeZoomLevel < 1.05) return;
      /* Side pane needs meaningful zoom; 1024px heroes often have only ~1.05–1.12 native headroom. */
      activeZoomLevel = Math.max(activeZoomLevel, 1.65);

      root.classList.add('msp-lightbox--magnify');
      revealMagnifierEl(lensEl);
      revealMagnifierEl(zoomPaneEl);
      syncViewportSize();

      var paneW = 0;
      var paneH = 0;
      var useSidePane = zoomPaneEl && viewportWidth() >= PANE_MIN_VIEWPORT;
      if (useSidePane) {
        paneW = Math.min(PANE_MAX_PX, Math.max(PANE_MIN_PX, Math.floor(viewportWidth() * 0.22)));
        paneH = Math.min(PANE_MAX_PX, Math.max(PANE_MIN_PX, Math.floor(viewportHeight() * 0.3)));
        zoomPaneEl.style.width = paneW + 'px';
        zoomPaneEl.style.height = paneH + 'px';
        zoomPaneEl.style.backgroundImage =
          'url(' + JSON.stringify(imgEl.currentSrc || imgEl.src) + ')';
      }

      function hideMagnifier() {
        root.classList.remove('is-magnifying');
        resetImageTransform();
        if (lensEl) lensEl.classList.remove('is-visible');
        if (zoomPaneEl) zoomPaneEl.classList.remove('is-visible');
      }

      function updateMagnifier(clientX, clientY) {
        var rect = imgEl.getBoundingClientRect();
        var cwNow = rect.width;
        var chNow = rect.height;
        if (!cwNow || !chNow) {
          hideMagnifier();
          return;
        }

        var x = clientX - rect.left;
        var y = clientY - rect.top;
        if (x < 0 || y < 0 || x > cwNow || y > chNow) {
          hideMagnifier();
          return;
        }

        var zoom = activeZoomLevel;

        if (lensEl) {
          var lensW = Math.max(LENS_MIN_PX, Math.min(LENS_MAX_PX, cwNow * LENS_RATIO));
          var lensH = Math.max(LENS_MIN_PX, Math.min(LENS_MAX_PX, chNow * LENS_RATIO));
          var lensLeft = Math.max(0, Math.min(cwNow - lensW, x - lensW / 2));
          var lensTop = Math.max(0, Math.min(chNow - lensH, y - lensH / 2));
          lensEl.style.width = Math.round(lensW) + 'px';
          lensEl.style.height = Math.round(lensH) + 'px';
          lensEl.style.left = Math.round(lensLeft) + 'px';
          lensEl.style.top = Math.round(lensTop) + 'px';
          revealMagnifierEl(lensEl);
          lensEl.classList.add('is-visible');
        }

        if (useSidePane && zoomPaneEl) {
          var relX = cwNow > 1 ? x / cwNow : 0;
          var relY = chNow > 1 ? y / chNow : 0;
          var bgW = nw * zoom;
          var bgH = nh * zoom;
          var bgX = -relX * (bgW - paneW);
          var bgY = -relY * (bgH - paneH);
          zoomPaneEl.style.backgroundSize = Math.round(bgW) + 'px ' + Math.round(bgH) + 'px';
          zoomPaneEl.style.backgroundPosition =
            Math.round(bgX) + 'px ' + Math.round(bgY) + 'px';
          revealMagnifierEl(zoomPaneEl);
          zoomPaneEl.classList.add('is-visible');
          positionZoomPane();
        }

        root.classList.add('is-magnifying');
      }

      magnifierLeave = function () {
        hideMagnifier();
      };
      magnifierMove = function (e) {
        updateMagnifier(e.clientX, e.clientY);
      };

      mediaEl.addEventListener('mousemove', magnifierMove);
      mediaEl.addEventListener('mouseenter', magnifierMove);
      mediaEl.addEventListener('mouseleave', magnifierLeave);
      imgEl.addEventListener('mousemove', magnifierMove);
      imgEl.addEventListener('mouseenter', magnifierMove);
      imgEl.addEventListener('mouseleave', magnifierLeave);
    }

    function fitImage() {
      if (!imgEl || !root.classList.contains('is-open')) return;

      teardownMagnifier();
      imgEl.style.maxWidth = '';
      imgEl.style.maxHeight = '';

      var nw = imgEl.naturalWidth;
      var nh = imgEl.naturalHeight;
      if (!nw || !nh) return;

      var box = getAvailableBox();
      var scale = Math.min(box.maxW / nw, box.maxH / nh, 1);
      var displayW = Math.max(1, Math.floor(nw * scale));
      var displayH = Math.max(1, Math.floor(nh * scale));

      imgEl.style.maxWidth = displayW + 'px';
      imgEl.style.maxHeight = displayH + 'px';

      /* Measure clientWidth/Height after layout before magnifier eligibility. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          syncViewportSize();
          setupMagnifierIfEligible(0);
        });
      });
    }

    function scheduleFit() {
      if (!imgEl || !root.classList.contains('is-open')) return;

      function runFit() {
        if (!root.classList.contains('is-open')) return;
        if (imgEl.complete && imgEl.naturalWidth) {
          fitImage();
        } else {
          imgEl.onload = function () {
            imgEl.onload = null;
            fitImage();
          };
        }
      }

      /* Let caption/layout settle before measuring chrome. */
      requestAnimationFrame(function () {
        requestAnimationFrame(runFit);
      });
    }

    function bindResize() {
      if (resizeHandler) return;
      resizeHandler = function () {
        if (root.classList.contains('is-open')) scheduleFit();
      };
      window.addEventListener('resize', resizeHandler);
    }

    function unbindResize() {
      if (!resizeHandler) return;
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }

    function updateNav() {
      var single = items.length <= 1;
      root.classList.toggle('msp-lightbox--single', single);
      if (prevBtn) prevBtn.disabled = single || index <= 0;
      if (nextBtn) nextBtn.disabled = single || index >= items.length - 1;
    }

    function render() {
      if (!items.length) return;
      var item = normalizeItem(items[index]);
      clearFit();
      if (imgEl) {
        imgEl.src = item.url;
        imgEl.alt = item.caption;
      }
      if (capEl) capEl.textContent = item.caption;
      updateNav();
      scheduleFit();
    }

    function setMode(mode) {
      root.classList.remove('msp-lightbox--swatch', 'msp-lightbox--gallery');
      if (mode === 'swatch') root.classList.add('msp-lightbox--swatch');
      else if (mode === 'gallery') root.classList.add('msp-lightbox--gallery');
    }

    function open(newItems, startIndex, mode) {
      items = (newItems || []).map(normalizeItem).filter(function (it) {
        return it.url;
      });
      if (!items.length) return;
      index = Math.max(0, Math.min(startIndex || 0, items.length - 1));
      setMode(mode || 'gallery');
      /* is-open before render: fitImage/setupMagnifierIfEligible require it (was skipped on first slide). */
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('msp-lightbox-open');
      document.body.style.overflow = 'hidden';
      render();
      bindResize();
      if (closeBtn) closeBtn.focus();
      bindKeys();
    }

    function close() {
      root.classList.remove(
        'is-open',
        'msp-lightbox--swatch',
        'msp-lightbox--gallery',
        'msp-lightbox--single',
        'msp-lightbox--magnify',
        'is-magnifying'
      );
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('msp-lightbox-open');
      document.body.style.overflow = '';
      unbindKeys();
      unbindResize();
      clearFit();
      items = [];
      index = 0;
      if (imgEl) imgEl.src = '';
      if (capEl) capEl.textContent = '';
    }

    function prev() {
      if (index > 0) {
        index -= 1;
        render();
      }
    }

    function next() {
      if (index < items.length - 1) {
        index += 1;
        render();
      }
    }

    function bindKeys() {
      if (keyHandler) return;
      keyHandler = function (e) {
        if (!root.classList.contains('is-open')) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          next();
        }
      };
      document.addEventListener('keydown', keyHandler);
    }

    function unbindKeys() {
      if (!keyHandler) return;
      document.removeEventListener('keydown', keyHandler);
      keyHandler = null;
    }

    function itemsFromSwatchGrid(grid) {
      var out = [];
      var swatches = grid.querySelectorAll('.msp-swatch:not(.msp-swatch--unavailable)');
      for (var i = 0; i < swatches.length; i++) {
        var sw = swatches[i];
        var img = sw.querySelector('.msp-swatch__chip img, img');
        if (!img || !img.src) continue;
        var codeEl = sw.parentElement && sw.parentElement.querySelector('.msp-swatch-code');
        var code = codeEl ? codeEl.textContent.trim() : '';
        var alt = img.getAttribute('alt') || '';
        var caption = alt;
        if (code && alt && caption.indexOf('(') === -1) {
          caption = alt + ' (' + code + ')';
        }
        out.push({ url: upgradeImageUrl(img.src), caption: caption });
      }
      return out;
    }

    function indexFromSwatch(el, grid) {
      var swatches = grid.querySelectorAll('.msp-swatch:not(.msp-swatch--unavailable)');
      for (var i = 0; i < swatches.length; i++) {
        if (swatches[i] === el || swatches[i].contains(el)) return i;
      }
      return 0;
    }

    function openFromSwatch(el, captionOverride) {
      if (window.event) window.event.stopPropagation();
      if (el.classList && el.classList.contains('msp-swatch--unavailable')) return;
      var grid = el.closest('.msp-finish-swatches');
      if (grid) {
        var gridItems = itemsFromSwatchGrid(grid);
        var idx = indexFromSwatch(el, grid);
        if (captionOverride && gridItems[idx]) {
          gridItems[idx].caption = captionOverride;
        }
        open(gridItems, idx, 'swatch');
        return;
      }
      var img = el.querySelector('.msp-swatch__chip img, img');
      if (img && img.src) {
        open(
        [{ url: upgradeImageUrl(img.src), caption: captionOverride || img.alt || '' }],
        0,
        'swatch'
      );
      }
    }

    function itemsFromCards(selector) {
      var out = [];
      var cards = document.querySelectorAll(selector);
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var img = card.querySelector('img');
        if (!img || !img.src) continue;
      out.push({
        url: upgradeImageUrl(img.src),
        caption: card.getAttribute('data-finish-name') || img.alt || '',
      });
      }
      return { items: out, cards: cards };
    }

    function openFromCard(card, selector) {
      var data = itemsFromCards(selector);
      var idx = 0;
      for (var i = 0; i < data.cards.length; i++) {
        if (data.cards[i] === card) {
          idx = i;
          break;
        }
      }
      open(data.items, idx, 'swatch');
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        prev();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        next();
      });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        close();
      });
    }
    if (backdrop) backdrop.addEventListener('click', close);
    root.addEventListener('click', function (e) {
      if (e.target === root) close();
    });

    var api = {
      open: open,
      close: close,
      prev: prev,
      next: next,
      openFromSwatch: openFromSwatch,
      openFromCard: openFromCard,
    };

    window.mspLightbox = api;
    window.mspFgOpen = function (url, name) {
      open([{ url: url, caption: name || '' }], 0, 'swatch');
    };
    window.openSwatch = function (el, name) {
      openFromSwatch(el, name);
    };
    window.closeSwatch = close;

    function initProductGallery() {
      var product = document.getElementById('product');
      if (!product) return;

      var galleryItems = [];
      var thumbLinks = product.querySelectorAll('.thumbs a');
      if (thumbLinks.length) {
        for (var i = 0; i < thumbLinks.length; i++) {
          var link = thumbLinks[i];
          var href =
            link.getAttribute('data-original-image') ||
            link.getAttribute('href');
          if (!href) continue;
          var thumbImg = link.querySelector('img');
          galleryItems.push({
            url: upgradeImageUrl(href),
            caption: (thumbImg && thumbImg.getAttribute('alt')) || '',
          });
        }
      } else {
        var placeholder = document.getElementById('placeholder');
        if (placeholder) {
          var phImg = placeholder.querySelector('img');
          galleryItems.push({
            url: upgradeImageUrl(placeholder.getAttribute('href') || ''),
            caption: (phImg && phImg.getAttribute('alt')) || '',
          });
        }
      }

      if (!galleryItems.length) return;

      function currentGalleryIndex() {
        var placeholder = document.getElementById('placeholder');
        if (!placeholder) return 0;
        var href = upgradeImageUrl(placeholder.getAttribute('href') || '');
        for (var j = 0; j < galleryItems.length; j++) {
          if (galleryItems[j].url === href) return j;
        }
        return 0;
      }

      function openGallery(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        open(galleryItems, currentGalleryIndex(), 'gallery');
        return false;
      }

      var triggers = product.querySelectorAll('#placeholder, #zoom-image, a.zoom');
      for (var t = 0; t < triggers.length; t++) {
        triggers[t].addEventListener('click', openGallery);
      }
    }

    function initFinishPages() {
      var wmCards = document.querySelectorAll('.msp-finishes-page--wm .msp-finish-card');
      if (wmCards.length) {
        wmCards.forEach(function (card) {
          card.addEventListener('click', function () {
            openFromCard(card, '.msp-finishes-page--wm .msp-finish-card');
          });
          card.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFromCard(card, '.msp-finishes-page--wm .msp-finish-card');
            }
          });
        });
      }
      var hofCards = document.querySelectorAll('.msp-finishes-page--hof .msp-finish-card');
      if (hofCards.length) {
        hofCards.forEach(function (card) {
          card.addEventListener('click', function () {
            openFromCard(card, '.msp-finishes-page--hof .msp-finish-card');
          });
          card.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFromCard(card, '.msp-finishes-page--hof .msp-finish-card');
            }
          });
        });
      }
    }

    initProductGallery();
    initFinishPages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
